import { useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Select,
    TextField,
    Theme,
    Typography,
    useTheme
} from '@material-ui/core';
import { Autocomplete, RenderInputParams } from '@material-ui/lab';
import { sortBy } from 'lodash';
import React from 'react';

import { JobType } from 'shared/common/job-constants';

import { DayPicker } from '../../core-ui/react-day-picker';
import { METRICS_FILTERS_DATA } from '../../graphql/queries/metrics';
import { JobTypes } from '../../state';
import {
    Duration,
    DurationOptionLabel,
    getConversionDurationOptions,
    getLabel,
    getTrendDurationOptions
} from './metrics-durations';
import { useMetricsDashboardData } from './use-metrics-dashboard-data';

interface DateRangeSelectorData {
    duration: Duration;
    onChange: (value: Duration) => void;
}

const styles = (theme: Theme) => css`
    .MuiDialogTitle-root {
        border-bottom: thin solid ${theme.palette.divider};
    }

    .MuiDialogActions-root {
        border-top: thin solid ${theme.palette.divider};
    }

    .settings-content {
        .settings-content-item {
            padding: 15px 0 20px;
            border-bottom: thin solid ${theme.palette.divider};

            &:last-child {
                border-bottom: none;
            }

            .settings-content-item-description {
                padding: 15px 0;
                display: flex;

                .date-field {
                    flex: 1 1 auto;
                    margin: 0 20px;

                    &:first-child {
                        margin-left: 0;
                    }

                    &:last-child {
                        margin-right: 0;
                    }
                }
            }
        }

        .settings-content-item-description-note {
            font-size: 12px;
            margin-top: -5px;
            opacity: 0.75;

            span {
                padding-right: 25px;
            }
        }
    }
`;

const dateRangeDialogStyles = (theme: Theme) => css`
    .pickers {
        .picker {
            margin: 10px 0 5px;
            padding: 10px 0;
            border-bottom: thin solid ${theme.palette.divider};

            .MuiTypography-h5 {
                text-align: center;
            }

            &:last-child {
                border-bottom: none;
                margin-bottom: 0;
                padding-bottom: 5px;
            }
        }
    }
`;

const DateRangeSelectorDialog: React.FC<{
    initialDuration: Duration;
    onChange: (value: Duration) => void;
    onClose: () => void;
    open: boolean;
}> = ({ initialDuration, onChange, onClose, open }) => {
    const [duration, setDuration] = React.useState(initialDuration);
    const theme = useTheme();

    const handleUpdate = () => {
        onChange(duration);
        onClose();
    };

    const handleRangeSelectorChange = (kind: 'main' | 'baseline') => (range: { from: Date; to: Date }) => {
        if (range) {
            const updated =
                kind === 'main'
                    ? { ...duration, start: range.from?.getTime(), end: range.to?.getTime() }
                    : { ...duration, baselineStart: range.from?.getTime(), baselineEnd: range.to?.getTime() };
            setDuration(updated);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} css={dateRangeDialogStyles(theme)}>
            <DialogContent>
                <div className="pickers">
                    <div className="picker">
                        <Typography variant="h5">Period</Typography>
                        <DayPicker
                            defaultMonth={new Date(duration.end)}
                            mode="range"
                            selected={{ from: new Date(duration.start), to: new Date(duration.end) }}
                            onSelect={handleRangeSelectorChange('main')}
                        />
                    </div>
                    <div className="picker">
                        <Typography variant="h5">Baseline</Typography>
                        <DayPicker
                            defaultMonth={new Date(duration.baselineEnd)}
                            mode="range"
                            selected={{ from: new Date(duration.baselineStart), to: new Date(duration.baselineEnd) }}
                            onSelect={handleRangeSelectorChange('baseline')}
                        />
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleUpdate}>Update</Button>
            </DialogActions>
        </Dialog>
    );
};

export const MetricsSettingsDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const theme = useTheme();
    const { data: filtersData } = useQuery<{
        accountManagers: Array<{ id: string; name: string }>;
        recruiters: Array<{ id: string; name: string }>;
        clients: Array<{ id: string; name: string }>;
        teams: Array<{
            id: string;
            name: string;
            lead: { id: string; name: string };
            users: Array<{ user: { id: string; name: string } }>;
        }>;
    }>(METRICS_FILTERS_DATA);
    const {
        conversionDuration,
        jobTypes,
        trendDuration,
        trendRollingTickMonths,
        trendRollingWindowMonths,
        trendUseCalendarMonthTotals,
        onConversionDurationChange,
        onTrendDurationChange,
        restrictToAccountManagerIds,
        restrictToClientIds,
        restrictToRecruiterIds,
        setJobTypes,
        setRestrictToAccountManagerIds,
        setRestrictToClientIds,
        setRestrictToRecruiterIds,
        setTrendRollingTickMonths,
        setTrendRollingWindowMonths,
        setTrendUseCalendarMonthTotals
    } = useMetricsDashboardData();
    const [customDateSelectorData, setCustomDateSelectorData] = React.useState<DateRangeSelectorData | undefined>(
        undefined
    );

    const handleCloseDateSelector = () => setCustomDateSelectorData(undefined);

    const handleSelectDateRange = (kind: 'conversion' | 'trend') => (label: DurationOptionLabel) => () => {
        if (label === 'Custom') {
            setCustomDateSelectorData({
                duration: kind === 'conversion' ? conversionDuration.value : trendDuration.value,
                onChange: (value) => {
                    if (kind === 'conversion') {
                        onConversionDurationChange(label, value);
                    } else {
                        onTrendDurationChange(label, value);
                    }
                }
            });
        } else {
            if (kind === 'conversion') {
                onConversionDurationChange(label, undefined);
            } else {
                onTrendDurationChange(label, undefined);
            }
        }
    };

    const handleSelectFilter = (updater: (ids: string[]) => void) => (_1: React.SyntheticEvent, newValue: string) => {
        if (!newValue) {
            updater(undefined);
        } else {
            updater([newValue]);
        }
    };

    const handleChangeJobTypes = (_1: React.SyntheticEvent, newValue: JobType[]) => {
        setJobTypes(newValue);
    };

    const handleTrendRollingWindowMonthsChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as string;
        if (value === 'tick') {
            setTrendRollingWindowMonths(null);
            return;
        }

        const windowMonths = Number(value);

        // A lookback equal to the tick is identical to "None (per tick)".
        if (!trendUseCalendarMonthTotals && windowMonths === trendRollingTickMonths) {
            setTrendRollingWindowMonths(null);
            return;
        }

        if (trendUseCalendarMonthTotals) {
            setTrendUseCalendarMonthTotals(false);
        }
        setTrendRollingWindowMonths(windowMonths);
    };

    const handleTrendTickChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as string;

        if (value === 'calendar') {
            setTrendUseCalendarMonthTotals(true);
            setTrendRollingWindowMonths(null);
            setTrendRollingTickMonths(1);
            return;
        }

        if (trendUseCalendarMonthTotals) {
            setTrendUseCalendarMonthTotals(false);
        }

        const tickMonths = Number(value);

        // If the current lookback becomes identical to the tick, normalize it to "None".
        if (trendRollingWindowMonths !== null && trendRollingWindowMonths === tickMonths) {
            setTrendRollingWindowMonths(null);
        }

        setTrendRollingTickMonths(tickMonths);
    };

    const dateSelectorDialog = customDateSelectorData ? (
        <DateRangeSelectorDialog
            initialDuration={customDateSelectorData.duration}
            onChange={customDateSelectorData.onChange}
            open={true}
            onClose={handleCloseDateSelector}
        />
    ) : null;

    const conversionMenuOpts = getConversionDurationOptions().map((opt) => (
        <MenuItem key={opt.label} value={opt.label} onClick={handleSelectDateRange('conversion')(opt.label)}>
            {opt.label}
        </MenuItem>
    ));

    const trendMenuOpts = getTrendDurationOptions().map((opt) => (
        <MenuItem key={opt.label} value={opt.label} onClick={handleSelectDateRange('trend')(opt.label)}>
            {opt.label}
        </MenuItem>
    ));

    const renderInput = (params: RenderInputParams) => <TextField {...params} variant="outlined" />;
    const getOptionLabel = (data: Array<{ id: string; name: string }>) => (option: string) =>
        data.find((d) => d.id === option)?.name ?? 'All';
    const getOptions = (data: Array<{ id: string; name: string }>) => data?.map((d) => d.id);

    const amSelector = filtersData?.accountManagers ? (
        <Autocomplete
            fullWidth={true}
            options={getOptions(filtersData?.accountManagers)}
            getOptionLabel={getOptionLabel(filtersData?.accountManagers)}
            renderInput={renderInput}
            value={restrictToAccountManagerIds?.[0] ?? ''}
            onChange={handleSelectFilter(setRestrictToAccountManagerIds)}
        />
    ) : null;

    const clientSelector = filtersData?.clients ? (
        <Autocomplete
            fullWidth={true}
            options={getOptions(filtersData?.clients)}
            getOptionLabel={getOptionLabel(filtersData?.clients)}
            renderInput={renderInput}
            value={restrictToClientIds?.[0] ?? ''}
            onChange={handleSelectFilter(setRestrictToClientIds)}
        />
    ) : null;

    const jobTypeOpts =
        sortBy(
            JobTypes.map((val, key) => ({ name: val.label, id: key }))
                .valueSeq()
                .toArray(),
            (v) => v.name
        ) ?? [];
    const jobTypeSelector = (
        <Autocomplete
            fullWidth={true}
            options={getOptions(jobTypeOpts)}
            getOptionLabel={getOptionLabel(jobTypeOpts)}
            renderInput={renderInput}
            value={jobTypes}
            multiple={true}
            onChange={handleChangeJobTypes}
        />
    );

    const recruiterSelector = filtersData?.recruiters ? (
        <Autocomplete
            fullWidth={true}
            options={getOptions(filtersData?.recruiters)}
            getOptionLabel={getOptionLabel(filtersData?.recruiters)}
            renderInput={renderInput}
            value={restrictToRecruiterIds?.[0] ?? ''}
            onChange={handleSelectFilter(setRestrictToRecruiterIds)}
        />
    ) : null;

    const allLookbackOptions = [
        { label: 'None (per tick)', value: 'tick' },
        { label: 'Last 1 month', value: '1' },
        { label: 'Last 2 months', value: '2' },
        { label: 'Last 3 months', value: '3' }
    ];

    const lookbackOptions = trendUseCalendarMonthTotals
        ? allLookbackOptions
        : allLookbackOptions.filter((opt) => opt.value === 'tick' || opt.value !== String(trendRollingTickMonths));

    const tickOptions = [
        { label: 'Monthly (rolling)', value: '1' },
        { label: 'Every 2 months (rolling)', value: '2' },
        { label: 'Every 3 months (rolling)', value: '3' },
        { label: 'Monthly (calendar)', value: 'calendar' }
    ];

    const lookbackMenuItems = lookbackOptions.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
        </MenuItem>
    ));

    const tickMenuItems = tickOptions.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
        </MenuItem>
    ));

    const normalizedRollingWindowMonths =
        !trendUseCalendarMonthTotals &&
        trendRollingWindowMonths !== null &&
        trendRollingWindowMonths === trendRollingTickMonths
            ? null
            : trendRollingWindowMonths;

    const lookbackSelectValue = normalizedRollingWindowMonths === null ? 'tick' : String(normalizedRollingWindowMonths);
    const tickSelectValue = trendUseCalendarMonthTotals ? 'calendar' : String(trendRollingTickMonths);

    const tickNote = trendUseCalendarMonthTotals
        ? 'Monthly (calendar)'
        : trendRollingTickMonths === 1
          ? 'Monthly (rolling)'
          : `Every ${trendRollingTickMonths} months (rolling)`;
    const lookbackNote = normalizedRollingWindowMonths === null ? 'None' : `${normalizedRollingWindowMonths} months`;

    return (
        <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="md" css={styles(theme)}>
            <DialogTitle>
                <Typography variant="h4">Dashboard Settings</Typography>
            </DialogTitle>
            <DialogContent>
                <div className="settings-content">
                    <div className="settings-content-item">
                        <Typography variant="h5">Trends</Typography>
                        <div className="settings-content-item-description">
                            <Select value={trendDuration.label} fullWidth={true} variant="outlined">
                                {trendMenuOpts}
                                <MenuItem value="Custom" onClick={handleSelectDateRange('trend')('Custom')}>
                                    Custom
                                </MenuItem>
                            </Select>
                        </div>
                        <div className="settings-content-item-description">
                            <div className="date-field">
                                <TextField
                                    select={true}
                                    label="Tick interval"
                                    value={tickSelectValue}
                                    fullWidth={true}
                                    variant="outlined"
                                    onChange={handleTrendTickChange}
                                >
                                    {tickMenuItems}
                                </TextField>
                            </div>
                            <div className="date-field">
                                <TextField
                                    select={true}
                                    label="Lookback window"
                                    value={lookbackSelectValue}
                                    fullWidth={true}
                                    variant="outlined"
                                    onChange={handleTrendRollingWindowMonthsChange}
                                >
                                    {lookbackMenuItems}
                                </TextField>
                            </div>
                        </div>
                        <div className="settings-content-item-description-note">
                            <span>Period: {getLabel(trendDuration.value.start, trendDuration.value.end)}</span>
                            <span>
                                Baseline: {getLabel(trendDuration.value.baselineStart, trendDuration.value.baselineEnd)}
                            </span>
                            <span>Tick: {tickNote}</span>
                            <span>Lookback: {lookbackNote}</span>
                        </div>
                    </div>
                    <div className="settings-content-item">
                        <Typography variant="h5">Conversions</Typography>
                        <div className="settings-content-item-description">
                            <Select value={conversionDuration.label} fullWidth={true} variant="outlined">
                                {conversionMenuOpts}
                                <MenuItem value="Custom" onClick={handleSelectDateRange('conversion')('Custom')}>
                                    Custom
                                </MenuItem>
                            </Select>
                        </div>
                        <div className="settings-content-item-description-note">
                            <span>
                                Period: {getLabel(conversionDuration.value.start, conversionDuration.value.end)}
                            </span>
                            <span>
                                Baseline:{' '}
                                {getLabel(conversionDuration.value.baselineStart, conversionDuration.value.baselineEnd)}
                            </span>
                        </div>
                    </div>
                    <div className="settings-content-item">
                        <Typography variant="h5">Client</Typography>
                        <div className="settings-content-item-description">{clientSelector}</div>
                    </div>
                    <div className="settings-content-item">
                        <Typography variant="h5">Job Types</Typography>
                        <div className="settings-content-item-description">{jobTypeSelector}</div>
                    </div>
                    <div className="settings-content-item">
                        <Typography variant="h5">Account Manager</Typography>
                        <div className="settings-content-item-description">{amSelector}</div>
                    </div>
                    <div className="settings-content-item">
                        <Typography variant="h5">Recruiter</Typography>
                        <div className="settings-content-item-description">{recruiterSelector}</div>
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
            {dateSelectorDialog}
        </Dialog>
    );
};
